//TODO : Refactor after ai
/** Modul 3 – Verkaufschance → Angebot → Auftrag. */
import { useNavigate, useParams } from "react-router-dom";
import { useDatenStore } from "@app/store";
import {
  auftragAusAngebot,
  datensatz,
  datensaetze,
  heute,
  rolle as rolleVon,
} from "@shared/demo-data/viacid";
import {
  angebotsSumme,
  datumCH,
  fr,
  mit,
  num,
  str,
  ziel,
  ziele,
  neueId,
} from "./lib";
import { Hinweis, Karte, Kennzahl, Knopf, Pille, Seite, Zeile } from "./ui";
import { ActionLeiste } from "./ActionLeiste";
import { objektKlasse as klasseVon } from "@shared/demo-data/viacid";

export function ChancenListePage() {
  const b = useDatenStore((s) => s.bestand);
  const nav = useNavigate();
  const chancen = datensaetze(b, "verkaufschance");
  const angebote = datensaetze(b, "angebot");
  const spalten: [string, string][] = [
    ["Identifiziert", "Identifiziert"],
    ["Qualifiziert", "Qualifiziert"],
    ["Angebot", "Angebot offen"],
    ["Gewonnen", "Gewonnen"],
  ];
  const inSpalte = (c: (typeof chancen)[number], key: string) => {
    const hatAngebot = angebote.some(
      (a) =>
        a.bezuege["angebot-zu-verkaufschance"] === c.id &&
        ["Versendet", "Änderung gewünscht"].includes(a.status ?? "")
    );
    if (key === "Angebot") return hatAngebot;
    if (key === "Qualifiziert")
      return c.status === "Qualifiziert" && !hatAngebot;
    return c.status === key;
  };
  return (
    <Seite
      titel="Chancen & Angebote"
      untertitel="Pipeline nach Phase · Klick öffnet die Verkaufschance"
    >
      <div className="vc-grid c4">
        {spalten.map(([key, label]) => {
          const liste = chancen.filter((c) => inSpalte(c, key));
          const vol = liste.reduce(
            (s, c) => s + num(c.werte.geschaetztesVolumen),
            0
          );
          return (
            <Karte key={key} titel={label} sub={`${liste.length} · ${fr(vol)}`}>
              <div className="vc-list">
                {liste.map((c) => (
                  <Zeile
                    key={c.id}
                    titel={c.titel}
                    meta={
                      ziel(b, c, "verkaufschance-betrifft-kunde", "kunde")
                        ?.titel
                    }
                    zu={`/app/chancen/${c.id}`}
                  />
                ))}
              </div>
            </Karte>
          );
        })}
      </div>
      <Karte titel="Alle Angebote" style={{ marginTop: 16 }}>
        <table className="vc-table">
          <thead>
            <tr>
              <th>Nr.</th>
              <th>Angebot</th>
              <th>Kunde</th>
              <th>Datum</th>
              <th className="num">Summe</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {angebote.map((a) => (
              <tr
                key={a.id}
                className="link"
                onClick={() => nav(`/app/angebote/${a.id}`)}
              >
                <td>{a.kennung}</td>
                <td>{a.titel}</td>
                <td>{ziel(b, a, "angebot-an-kunde", "kunde")?.titel}</td>
                <td>{datumCH(a.werte.angebotsdatum)}</td>
                <td className="num">{fr(angebotsSumme(b, a.id))}</td>
                <td>
                  <Pille status={a.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Karte>
    </Seite>
  );
}

export function ChancePage() {
  const { id = "" } = useParams();
  const b = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const aendern = useDatenStore((s) => s.aendern);
  const anlegen = useDatenStore((s) => s.anlegen);
  const nav = useNavigate();
  const c = datensatz(b, "verkaufschance", id);
  if (!c) return <Hinweis ton="red">Verkaufschance nicht gefunden.</Hinweis>;
  const kunde = ziel(b, c, "verkaufschance-betrifft-kunde", "kunde");
  const strassen = ziele(
    b,
    c,
    "verkaufschance-betrifft-strassenobjekt",
    "strassenobjekt"
  );
  const kundenStrassen = kunde
    ? mit(b, "strassenobjekt", "strassenobjekt-gehoert-zu-kunde", kunde.id)
    : [];
  const aufnahmen = mit(
    b,
    "baustellenaufnahme",
    "baustellenaufnahme-fuer-verkaufschance",
    c.id
  );
  const angebote = mit(b, "angebot", "angebot-zu-verkaufschance", c.id);
  const aufgaben = mit(b, "aufgabe", "aufgabe-betrifft-verkaufschance", c.id);
  const kontakte = mit(
    b,
    "kundenkontakt",
    "kundenkontakt-betrifft-verkaufschance",
    c.id
  );
  const offen = ["Identifiziert", "Qualifiziert"].includes(c.status ?? "");
  const persona = rolleVon(rolle)?.persona ?? null;

  const aufnahmeStarten = () => {
    const laufend = aufnahmen.find((a) => a.status !== "Bewertet");
    if (laufend) {
      nav(`/app/aufnahme/${laufend.id}`);
      return;
    }
    const so = strassen[0] ?? kundenStrassen[0];
    if (!so) {
      aendern(
        { slug: "verkaufschance", id: c.id },
        "Kein Strassenobjekt beim Kunden – zuerst unter Daten anlegen."
      );
      return;
    }
    const nid = neueId("ba");
    anlegen(
      "baustellenaufnahme",
      {
        id: nid,
        titel: `Aufnahme ${so.titel}`,
        untertitel: `${heute()} · neu`,
        kennung: null,
        status: "In Aufnahme",
        werte: {
          aufnahmedatum: heute(),
          abschnitt: "",
          gemesseneLaenge: 0,
          gemesseneBreite: 0,
          vorausmass: 0,
        },
        bezuege: {
          "baustellenaufnahme-betrifft-strassenobjekt": so.id,
          "baustellenaufnahme-fuer-verkaufschance": c.id,
          ...(persona
            ? { "baustellenaufnahme-erstellt-von-mitarbeiter": persona }
            : {}),
        },
      },
      "Aufnahme gestartet – jetzt auf dem Tablet erfassen."
    );
    nav(`/app/aufnahme/${nid}`);
  };
  const terminAnlegen = () => {
    anlegen(
      "aufgabe",
      {
        id: neueId("af"),
        titel: `Vor-Ort-Termin ${kunde?.titel ?? ""}`,
        untertitel: "Besuch",
        kennung: null,
        status: "Offen",
        werte: {
          titel: `Vor-Ort-Termin ${kunde?.titel ?? ""}`,
          aufgabenart: "Besuch",
          faelligAm: heute(),
          prioritaet: "hoch",
        },
        bezuege: {
          "aufgabe-betrifft-verkaufschance": c.id,
          ...(kunde ? { "aufgabe-betrifft-kunde": kunde.id } : {}),
          ...(persona ? { "aufgabe-zugewiesen-an-mitarbeiter": persona } : {}),
        },
      },
      "Aufgabe angelegt."
    );
  };

  return (
    <Seite
      titel={c.titel}
      untertitel={
        <>
          {kunde?.titel} · {str(c.werte.geschaeftsbereich)} ·{" "}
          {str(c.werte.saisonfenster)} <Pille status={c.status} />
        </>
      }
      aktionen={
        offen && (
          <>
            {c.status === "Identifiziert" && (
              <Knopf
                onClick={() =>
                  aendern(
                    {
                      slug: "verkaufschance",
                      id: c.id,
                      status: "Qualifiziert",
                    },
                    "Verkaufschance qualifiziert."
                  )
                }
              >
                Qualifizieren
              </Knopf>
            )}
            <Knopf onClick={terminAnlegen}>Vor-Ort-Termin</Knopf>
            <Knopf art="primary" onClick={aufnahmeStarten}>
              Aufnahme starten
            </Knopf>
            {klasseVon("verkaufschance") && (
              <ActionLeiste
                klasse={klasseVon("verkaufschance")!}
                satz={c}
                ausser={["Verkaufschance qualifizieren"]}
                verwandt={[
                  { slug: "aufgabe", rel: "aufgabe-betrifft-verkaufschance" },
                  {
                    slug: "kundenkontakt",
                    rel: "kundenkontakt-betrifft-verkaufschance",
                  },
                ]}
              />
            )}
          </>
        )
      }
    >
      <div className="vc-grid c3" style={{ marginBottom: 16 }}>
        <Kennzahl
          label="Geschätztes Volumen"
          wert={fr(num(c.werte.geschaetztesVolumen))}
          ton="accent"
        />
        <Kennzahl label="Aufnahmen" wert={aufnahmen.length} />
        <Kennzahl label="Angebote" wert={angebote.length} />
      </div>
      <div className="vc-grid main-side">
        <div className="vc-grid">
          <Karte titel="Bedarf">
            <p style={{ margin: 0, fontSize: "0.9rem" }}>
              {str(c.werte.bedarfsbeschreibung) || "—"}
            </p>
            <div className="sub" style={{ marginTop: 8 }}>
              Anlass: {str(c.werte.akquiseanlass) || "—"} · Strassenobjekte:{" "}
              {strassen.map((s) => s.titel).join(", ") || "—"}
            </div>
          </Karte>
          <Karte titel="Aufnahmen">
            <div className="vc-list">
              {aufnahmen.map((a) => (
                <Zeile
                  key={a.id}
                  titel={a.titel}
                  meta={`${datumCH(a.werte.aufnahmedatum)} · ${str(a.werte.abschnitt)}`}
                  rechts={<Pille status={a.status} />}
                  zu={`/app/aufnahme/${a.id}`}
                />
              ))}
              {aufnahmen.length === 0 && (
                <div className="sub">
                  Noch keine Aufnahme – „Aufnahme starten“.
                </div>
              )}
            </div>
          </Karte>
          <Karte titel="Angebote">
            <div className="vc-list">
              {angebote.map((a) => (
                <Zeile
                  key={a.id}
                  titel={`${a.kennung} · ${a.titel}`}
                  meta={`${datumCH(a.werte.angebotsdatum)} · ${fr(angebotsSumme(b, a.id))}`}
                  rechts={<Pille status={a.status} />}
                  zu={`/app/angebote/${a.id}`}
                />
              ))}
            </div>
          </Karte>
        </div>
        <div className="vc-grid">
          <Karte titel="Aufgaben">
            <div className="vc-list">
              {aufgaben.map((a) => (
                <Zeile
                  key={a.id}
                  titel={a.titel}
                  meta={`fällig ${datumCH(a.werte.faelligAm)}`}
                  rechts={<Pille status={a.status} />}
                />
              ))}
              {aufgaben.length === 0 && (
                <div className="sub">Kein Follow-up.</div>
              )}
            </div>
          </Karte>
          <Karte titel="Kontakte">
            <div className="vc-list">
              {kontakte.map((k) => (
                <Zeile
                  key={k.id}
                  titel={`${str(k.werte.kontaktart)} · ${str(k.werte.anlass)}`}
                  meta={`${datumCH(k.werte.datum)} · ${str(k.werte.ergebnis)}`}
                />
              ))}
            </div>
          </Karte>
        </div>
      </div>
    </Seite>
  );
}

export function AngebotPage() {
  const { id = "" } = useParams();
  const b = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const aendern = useDatenStore((s) => s.aendern);
  const anwenden = useDatenStore((s) => s.anwenden);
  const nav = useNavigate();
  const a = datensatz(b, "angebot", id);
  if (!a) return <Hinweis ton="red">Angebot nicht gefunden.</Hinweis>;
  const kunde = ziel(b, a, "angebot-an-kunde", "kunde");
  const positionen = mit(
    b,
    "angebotsposition",
    "angebotsposition-gehoert-zu-angebot",
    a.id
  );
  const auftrag = datensaetze(b, "auftrag").find(
    (x) => x.bezuege["auftrag-aus-angebot"] === a.id
  );
  const chance = ziel(b, a, "angebot-zu-verkaufschance", "verkaufschance");
  const extern = rolle === "KO" || rolle === "HA";

  const annehmen = () => {
    const aend = auftragAusAngebot(b, a.id);
    anwenden(aend);
    const neu = aend.neu.find((n) => n.slug === "auftrag");
    if (neu) nav(`/app/auftraege/${neu.satz.id}`);
  };

  return (
    <Seite
      titel={`${a.kennung} · ${a.titel}`}
      untertitel={
        <>
          {str(a.werte.dokumentart)} an {kunde?.titel} ·{" "}
          {datumCH(a.werte.angebotsdatum)} · gültig bis{" "}
          {datumCH(a.werte.gueltigBis)} <Pille status={a.status} />
        </>
      }
      aktionen={
        <>
          {chance && (
            <Knopf onClick={() => nav(`/app/chancen/${chance.id}`)}>
              Verkaufschance
            </Knopf>
          )}
          {!extern &&
            (a.status === "In Erstellung" ||
              a.status === "Änderung gewünscht") && (
              <Knopf
                art="primary"
                onClick={() =>
                  aendern(
                    {
                      slug: "angebot",
                      id: a.id,
                      status: "Versendet",
                      werte: { versendetAm: heute() },
                    },
                    "Angebot per E-Mail versendet."
                  )
                }
              >
                Versenden
              </Knopf>
            )}
          {extern &&
            ["Versendet", "Änderung gewünscht"].includes(a.status ?? "") && (
              <>
                <Knopf
                  onClick={() =>
                    aendern(
                      {
                        slug: "angebot",
                        id: a.id,
                        status: "Abgelehnt",
                        werte: { entschiedenAm: heute() },
                      },
                      "Angebot abgelehnt."
                    )
                  }
                >
                  Ablehnen
                </Knopf>
                <Knopf art="primary" onClick={annehmen}>
                  Angebot annehmen
                </Knopf>
              </>
            )}
          {auftrag && (
            <Knopf
              art="primary"
              onClick={() => nav(`/app/auftraege/${auftrag.id}`)}
            >
              Auftrag {auftrag.kennung}
            </Knopf>
          )}
          {klasseVon("angebot") && (
            <ActionLeiste
              klasse={klasseVon("angebot")!}
              satz={a}
              ausser={[
                "Angebot versenden",
                "Angebot annehmen",
                "Angebot ablehnen",
              ]}
            />
          )}
        </>
      }
    >
      {a.status === "Versendet" && !extern && (
        <div style={{ marginBottom: 16 }}>
          <Hinweis>
            Zum Annehmen oben rechts „Ansicht als Kunde (Kommune)“ wählen – im
            Prototyp ist die Kundenantwort simuliert.
          </Hinweis>
        </div>
      )}
      <div className="vc-grid main-side">
        <Karte titel="Positionen">
          <table className="vc-table">
            <thead>
              <tr>
                <th>Pos.</th>
                <th>Bezeichnung</th>
                <th className="num">Menge</th>
                <th>Einheit</th>
                <th className="num">Einzelpreis</th>
                <th className="num">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {positionen.map((p) => (
                <tr key={p.id}>
                  <td>{str(p.werte.positionsnummer)}</td>
                  <td>{str(p.werte.bezeichnung)}</td>
                  <td className="num">{num(p.werte.menge)}</td>
                  <td>{str(p.werte.einheit)}</td>
                  <td className="num">{fr(num(p.werte.einzelpreis))}</td>
                  <td className="num">
                    {fr(
                      num(p.werte.menge) *
                        num(p.werte.einzelpreis) *
                        (1 - num(p.werte.rabatt) / 100)
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={5} style={{ textAlign: "right", fontWeight: 600 }}>
                  Summe netto
                </td>
                <td className="num" style={{ fontWeight: 600 }}>
                  {fr(angebotsSumme(b, a.id))}
                </td>
              </tr>
            </tbody>
          </table>
        </Karte>
        <div className="vc-grid">
          <Karte titel="Leistungsbeschreibung">
            <p style={{ margin: 0, fontSize: "0.9rem" }}>
              {str(a.werte.leistungsbeschreibung) || "—"}
            </p>
          </Karte>
          <Karte titel="Kalkulationsannahmen" sub="Grundlage des Preises">
            <p
              style={{
                margin: 0,
                fontSize: "0.85rem",
                color: "var(--vc-muted)",
              }}
            >
              {str(a.werte.kalkulationsannahmen) ||
                "aus den verknüpften Kalkulationen"}
            </p>
          </Karte>
        </div>
      </div>
    </Seite>
  );
}
